import React from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import UserInitialsAvatar from 'components/UserInitialsAvatar/UserInitialsAvatar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useUserInitials } from 'features/auth/hooks/useAuthData';
import { useLogout } from 'features/navbar/hooks/useLogout';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { Logout, Settings } from '@mui/icons-material';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { visuallyHidden } from '@mui/utils';

const StyledExpandMoreIcon = styled(ExpandMoreIcon)(({ theme }) => ({
  color: theme.palette.grey[500],
  transition: 'all 0.1s linear',
}));

const AccountMenu = () => {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const userInitials = useUserInitials();
  const isMenuOpen = Boolean(menuAnchor);
  const { handleLogout } = useLogout();
  const { translation } = useTranslationContext();
  const navigate = useNavigate();

  const menuItemSettingsId = 'account-menu_settings-item';
  const menuItemLogoutId = 'account-menu_logout-item';
  const accountMenuId = 'account-menu_id';

  const navigateToSettings = () => {
    navigate('/settings');
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Box>
      <IconButton onClick={handleClick} aria-labelledby={accountMenuId}>
        <UserInitialsAvatar initials={userInitials} hasGradientBackgroundColor />
        <Typography sx={visuallyHidden} id={accountMenuId}>
          Open account menu
        </Typography>
        <StyledExpandMoreIcon
          sx={{ transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          aria-hidden="true"
        />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { elevation: 4, sx: { marginTop: 1.5 } } }}
        disableEnforceFocus
      >
        <MenuItem onClick={navigateToSettings} aria-labelledby={menuItemSettingsId}>
          <ListItemIcon>
            <Settings sx={{ color: 'grey.700' }} />
          </ListItemIcon>
          <Typography
            variant="inherit"
            color="grey.700"
            textTransform="capitalize"
            id={menuItemSettingsId}
          >
            {translation('general_permission_settings_link_text')}
          </Typography>
        </MenuItem>
        <Divider sx={{ color: 'grey.400', marginX: 1 }} aria-hidden="true" />
        <MenuItem onClick={handleLogout} aria-labelledby={menuItemLogoutId}>
          <ListItemIcon>
            <Logout color="error" />
          </ListItemIcon>
          <Typography
            variant="inherit"
            color="error"
            textTransform="capitalize"
            id={menuItemLogoutId}
          >
            {translation('log_out')}
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AccountMenu;
